import { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import { ExerciseTranslationsContext, ExerciseTranslationsContextValue } from './contexts/ExerciseTranslationsContext';
import { TokenAccessor } from '../../types/API';
import { ExercisePhase, ExerciseResult } from './@types/exercise-types';
import { ExerciseManagerContextProvider, useExerciseManagerContext } from './contexts/ExerciseManagerContext';
import { AnalyticsData } from '../../hooks/constants/Analytics';
import { SupportedUserLanguage } from '@pelckmans/business-components/context/LanguageContext';
import { BearerTokenGetterContext } from '@pelckmans/business-components/context/BearerTokenGetterContext';
import { Module } from '../../../../api/module';
import { Medialink } from '../../../../api/medialink';
import ExerciseLaunchModals from './components/exercise-launch-modals';
import {
  initAssignmentSession,
  InitExerciseSessionResponse,
  initFreeExerciseSession,
  PauseExerciseSessionResponse,
  StopExerciseSessionResponse,
} from './api/exercises-api';
import ExerciseActivityModals from './components/exercise-activity-modals';
import ExerciseProgressionModals from './components/exercise-progression-modals';

import './exercise-dialogs.css';

type ExerciseManagerProps = {
  translations: ExerciseTranslationsContextValue;
  language: SupportedUserLanguage;
  medialink: Medialink;
  tokenAccessor: TokenAccessor;
  initialPhase?: ExercisePhase;
  assignmentId?: string;
  onError: (err: unknown) => void;
  onClose: () => void;
  onSessionPaused?: (result: ExerciseResult) => void;
  onSessionFinished?: (result: ExerciseResult) => void;
  analyticsData?: AnalyticsData;
  studioGroup: string;
  selectedModule: Module;
};

const ExerciseManagerWrappedWithContext = ({
  medialink,
  translations,
  language,
  onError,
  tokenAccessor,
  onClose,
  initialPhase,
  assignmentId,
  onSessionPaused,
  onSessionFinished,
  analyticsData,
  studioGroup,
  selectedModule,
}: ExerciseManagerProps): JSX.Element => {
  const [phaseAction, setPhaseAction] = useState<ExercisePhase | undefined>(initialPhase);

  useEffect(() => {
    if (initialPhase) setPhaseAction(initialPhase);
  }, [initialPhase]);

  return (
    <ExerciseTranslationsContext.Provider value={translations}>
      <BearerTokenGetterContext.Provider value={tokenAccessor.tokenGetter}>
        <ExerciseManagerContextProvider
          medialink={medialink}
          module={selectedModule}
          onClose={onClose}
          studioGroup={studioGroup}
          language={language}
          assignmentId={assignmentId}
        >
          <ExerciseManager
            onError={onError}
            setPhaseAction={setPhaseAction}
            analyticsData={analyticsData}
            phaseAction={phaseAction}
            onSessionPaused={onSessionPaused}
            onSessionFinished={onSessionFinished}
          />
        </ExerciseManagerContextProvider>
      </BearerTokenGetterContext.Provider>
    </ExerciseTranslationsContext.Provider>
  );
};

interface Props
  extends Pick<
    ExerciseManagerProps,
    'onError' | 'analyticsData' | 'onSessionPaused' | 'onSessionFinished' | 'initialPhase'
  > {
  phaseAction: ExercisePhase | undefined;
  setPhaseAction: Dispatch<SetStateAction<ExercisePhase | undefined>>;
}

const ExerciseManager = ({
  onError,
  setPhaseAction,
  phaseAction,
  analyticsData,
  onSessionPaused,
  onSessionFinished,
}: Props) => {
  const getBearerToken = useContext(BearerTokenGetterContext);

  const { phase, setPhase, medialink, module, studioGroup, assignmentId } = useExerciseManagerContext();

  const [exerciseSession, setExerciseSession] = useState<InitExerciseSessionResponse>();
  const [exercisePausedSession, setExercisePausedSession] = useState<PauseExerciseSessionResponse>();
  const [exerciseFinishedSession, setExerciseFinishedSession] = useState<StopExerciseSessionResponse>();

  const [reviewSessionId, setReviewSessionId] = useState<string>();

  useEffect(() => {
    if (reviewSessionId) {
      // phase action is reset to undefined when the reviewSessionId is set, so this will rerender
      return;
    }

    async function getExerciseSessionInfo(medialink: Medialink) {
      const token = await getBearerToken();

      return assignmentId
        ? initAssignmentSession(token!, module.id, assignmentId, studioGroup)
        : initFreeExerciseSession(token!, module.id, medialink.id, studioGroup);
    }

    if (!exerciseSession) {
      getExerciseSessionInfo(medialink)
        .then(exerciseSession => {
          setExerciseSession(exerciseSession);

          if (exerciseSession.result) {
            setPhase('launch');
          } else if (exerciseSession) {
            setPhase('activity');
            setPhaseAction('start');
          }
        })
        .catch(onError);
    }
  }, [
    assignmentId,
    module.id,
    module,
    getBearerToken,
    setPhase,
    onError,
    studioGroup,
    medialink,
    setPhaseAction,
    phaseAction,
    reviewSessionId,
    exerciseSession,
  ]);

  const handleSessionPaused = (session: PauseExerciseSessionResponse) => {
    setExercisePausedSession(session);

    if (onSessionPaused) onSessionPaused(session.result);
  };

  const handleSessionFinished = (session: StopExerciseSessionResponse) => {
    setExerciseFinishedSession(session);

    if (onSessionFinished) onSessionFinished(session.result);
  };

  const handleResume = () => {
    setPhaseAction('resume');
    setPhase('activity');
    setExercisePausedSession(undefined);
  };

  const handleRestart = () => {
    setPhaseAction('start');
    setPhase('activity');
    setExercisePausedSession(undefined);
    setExerciseFinishedSession(undefined);
  };

  const handleReview = (sessionId: string) => {
    setReviewSessionId(sessionId);
    setPhaseAction(undefined);
    setPhase('activity');
  };

  return (
    <>
      {phase === 'launch' && (
        <ExerciseLaunchModals
          exerciseSession={exerciseSession}
          onReview={handleReview}
          onRestart={handleRestart}
          onResume={handleResume}
        />
      )}
      {phase === 'activity' && (
        <ExerciseActivityModals
          reviewSessionId={reviewSessionId}
          phaseAction={phaseAction}
          analyticsData={analyticsData}
          onSessionPaused={handleSessionPaused}
          onSessionFinished={handleSessionFinished}
          setPhase={setPhase}
        />
      )}
      {phase === 'progression' && (
        <ExerciseProgressionModals
          pausedSession={exercisePausedSession}
          finishedSession={exerciseFinishedSession}
          onResume={handleResume}
          onRestart={handleRestart}
          onReview={handleReview}
        />
      )}
    </>
  );
};

export { ExerciseManagerWrappedWithContext as ExerciseManager };
